import { useState } from 'react';
import { AiFillWarning } from 'react-icons/ai';

import useTranslations from '@/i18n/useTranslations';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import ActionRejectedPopup from '@/shared-components/popups/components/ActionRejectedPopup';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';

import Audio from './audio';
import MainPicture from './main-picture';
import SecondaryPictures from './secondary-pictures';

import './index.css';

export default function Media({
  formState,
  setFormState,
  editMode,
  previewMode,
  formError,
  setFormError,
  permissions = {},
  galleryPermissionCheck = null,
}) {
  const t = useTranslations('common.forms.add_artwork');
  const [showDeniedPopup, setShowDeniedPopup] = useState(false);
  const [showActionRejectedPopup, setShowActionRejectedPopup] = useState(false);
  const mainPictureMissing = Array.isArray(formError)
    && formError.some((error) => error.element === 'artwork_main_picture' && error.error_code === 'EMPTY_FIELD');

  const hasArtistPerms = editMode && Object.keys(permissions).length > 0;
  const isGalleryMember = typeof galleryPermissionCheck === 'function';
  const canGallery = key => (!isGalleryMember ? true : galleryPermissionCheck(key));
  const canEditMainPicture = hasArtistPerms ? !!permissions.mainPicture : canGallery('artworks.edit_images');
  const canEditSecondaryPictures = hasArtistPerms
    ? !!permissions.secondaryPictures
    : canGallery('artworks.edit_secondary_images');
  const canEditAudio = hasArtistPerms ? !!permissions.audio : canGallery('artworks.edit_audio');
  const triggerDeniedPopup = () => {
    if (isGalleryMember) {
      setShowActionRejectedPopup(true);
      return;
    }
    setShowDeniedPopup(true);
  };

  function onInputMediaData(key, value) {
    setFormState(prevValue => {
      return {
        ...prevValue, 
        artwork_media: {
          ...prevValue.artwork_media, 
          [key]: value
        }
      };
    });

    setFormError(prevErrors => {
      if (prevErrors === null) {
        return [];
      } else {
        return prevErrors.filter(error => error.element !== key);
      }
    });
  }

  const wrapDisabled = (disabled, children) => (
    <div
      onClick={e => {
        if (disabled) {
          e.preventDefault();
          triggerDeniedPopup();
        }
      }}
      // evita que el div bloquee el scroll interno o selects
      style={{ display: 'contents' }}
    >
      {children}
    </div>
  );

  return (
    <>
      <div className="add-artwork-media-form">
        {/*
          Si can('mainPicture') es false → envuelve MainPicture en nuestro div
        */}
        {wrapDisabled(
          !canEditMainPicture,
          <MainPicture
            element="artwork_main_picture"
            formError={formError}
            setFormError={setFormError}
            previewMode={previewMode}
            fillWith={formState.artwork_media.artwork_main_picture}
            onChange={v => onInputMediaData('artwork_main_picture', v)}
            disabled={!canEditMainPicture}
          />
        )}

        {wrapDisabled(
          !canEditSecondaryPictures,
          <SecondaryPictures
            formError={formError}
            setFormError={setFormError}
            previewMode={previewMode}
            fillWith={formState.artwork_media.artwork_secondary_pictures}
            onChange={v => onInputMediaData('artwork_secondary_pictures', v)}
            disabled={!canEditSecondaryPictures}
          />
        )}

        {wrapDisabled(
          !canEditAudio,
          <Audio
            formError={formError}
            setFormError={setFormError}
            previewMode={previewMode}
            fillWith={formState.artwork_media.artwork_audio}
            onChange={v => onInputMediaData('artwork_audio', v)}
            disabled={!canEditAudio}
          />
        )}
      </div>

      <div className="add-artwork-media-form__audio">
        {mainPictureMissing && (
          <div className="form-error__message-container active">
            <AiFillWarning className="form-error__message-icon" />
            <p className="form-error__message-text">
              {t('media.missing_main_picture')}
            </p>
          </div>
        )}
      </div>
      <GenericPopup
        open={showDeniedPopup}
        onClick={() => setShowDeniedPopup(false)}
        opacity
        className="generic-popup_content"
      >
        <div className="generic-popup__header">
          <XIcon
            className="generic-popup__close"
            onClick={() => setShowDeniedPopup(false)}
          />
          <Text className="generic-popup__header-text-theme">
            {t('media.field_locked_title')}
          </Text>
        </div>
        <p>{t('media.field_locked_description')}</p>
      </GenericPopup>
      <ActionRejectedPopup
        open={showActionRejectedPopup}
        close={() => setShowActionRejectedPopup(false)}
        description={t('media.permission_denied')}
      />
    </>
  );
}
